import React from 'react';
import styled from 'styled-components';
import SEO from '../../components/common/SEO';
import { useTranslation } from 'react-i18next';
import theme from '../../theme';

const PrivacySection = styled.section`
  padding: 6rem 0 4rem;
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const PageTitle = styled.h1`
  font-size: ${theme.fontSizes.xxlarge};
  margin-bottom: 1rem;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background-color: ${theme.colors.primary};
  }
`;

const PolicyContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  p {
    margin-bottom: 1.5rem;
    line-height: 1.7;
  }
  
  ul, ol {
    margin-bottom: 1.5rem;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.75rem;
      line-height: 1.7;
    }
  }
`;

const PolicySectionBlock = styled.div`
  margin-bottom: 3rem;
`;

const PolicySectionTitle = styled.h2`
  margin-bottom: 1.5rem;
  font-size: ${theme.fontSizes.xlarge};
  color: ${theme.colors.secondary};
  
  &:after {
    content: '';
    display: block;
    width: 50px;
    height: 3px;
    background-color: ${theme.colors.primary};
    margin-top: 0.75rem;
  }
`;

const SubSectionTitle = styled.h3`
  margin: 2rem 0 1rem;
  font-size: ${theme.fontSizes.large};
  color: ${theme.colors.secondary};
`;

const LastUpdated = styled.div`
  text-align: center;
  margin-top: 3rem;
  padding-top: 2rem;
  border-top: 1px solid ${theme.colors.lightGray};
  color: ${theme.colors.darkGray};
  font-size: ${theme.fontSizes.small};
`;

const PrivacyPolicyPage = () => {
  const { t } = useTranslation();
  const p = 'privacyPolicyPage';

  return (
    <>
      <SEO titleKey={`${p}.seo.title`} descriptionKey={`${p}.seo.description`} />
      
      <PrivacySection>
        <div className="container">
          <PageHeader>
            <PageTitle>{t(`${p}.pageTitle`)}</PageTitle>
          </PageHeader>
          
          <PolicyContent>
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.intro.title`)}</PolicySectionTitle>
              <p>{t(`${p}.intro.paragraph1`)}</p>
              <p>{t(`${p}.intro.paragraph2`)}</p>
            </PolicySectionBlock>
            
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.scope.title`)}</PolicySectionTitle>
              <p>{t(`${p}.scope.paragraph1`)}</p>
            </PolicySectionBlock>
            
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.principles.title`)}</PolicySectionTitle>
              <ul>
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.principles.confidentiality`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.principles.integrity`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.principles.availability`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.principles.accountability`) }} />
              </ul>
            </PolicySectionBlock>
            
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.measures.title`)}</PolicySectionTitle>
              
              <SubSectionTitle>{t(`${p}.measures.technical.title`)}</SubSectionTitle>
              <ul>
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.technical.encryption`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.technical.firewall`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.technical.ddos`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.technical.ssl`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.technical.backup`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.technical.updates`) }} />
              </ul>
              
              <SubSectionTitle>{t(`${p}.measures.organizational.title`)}</SubSectionTitle>
              <ul>
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.organizational.training`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.organizational.access`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.organizational.testing`) }} />
                <li dangerouslySetInnerHTML={{ __html: t(`${p}.measures.organizational.incident`) }} />
              </ul>
            </PolicySectionBlock>
            
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.dataProcessing.title`)}</PolicySectionTitle>
              <p>{t(`${p}.dataProcessing.paragraph1`)}</p>
              <p>{t(`${p}.dataProcessing.paragraph2`)}</p>
            </PolicySectionBlock>
            
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.breach.title`)}</PolicySectionTitle>
              <p>{t(`${p}.breach.paragraph1`)}</p>
            </PolicySectionBlock>
            
            <PolicySectionBlock>
              <PolicySectionTitle>{t(`${p}.contact.title`)}</PolicySectionTitle>
              <p>{t(`${p}.contact.paragraph1`)}</p>
              <p>
                <strong>E-posta:</strong> {t(`${p}.contact.email`)}<br />
                <strong>Telefon:</strong> {t(`${p}.contact.phone`)}
              </p>
            </PolicySectionBlock>
            
            <LastUpdated>
              {t(`${p}.lastUpdated`)}
            </LastUpdated>
          </PolicyContent>
        </div>
      </PrivacySection>
    </>
  );
};

export default PrivacyPolicyPage;
